import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { identifyUser, trackTrialStarted, trackTrialCheckoutViewed, trackTrialSubscribed } from '../lib/analytics';
import { useScreenInit } from '../useScreenInit.js';
import { ArrowRightIcon, ArrowLeftIcon, CheckCircle2Icon, MessageCircleIcon, StarIcon, PauseIcon, VolumeXIcon, MaximizeIcon, SettingsIcon, ShieldCheckIcon, ChevronDownIcon, CreditCardIcon, LandmarkIcon } from 'lucide-react';
// ─────────────────────────────────────────────────────────────────────────
// Alternate sign-in / free-trial flow for Giga — a self-contained page with an
// internal 4-step state machine, recreating the structure of the reference
// flow but fully rebranded to Giga's warm espresso / cream / orange language.
//
//   account  → the "Let's get you started" card over a blurred workspace
//   checkout → plan picker + payment + order summary + what's-included + FAQ
//   (3ds)    → a bank authorisation modal that overlays the checkout
//   welcome  → "Welcome to Giga, Faith!" welcome-video + goal question
//
// This is ADDITIVE. The production flow at /signup and /login is untouched.
// ─────────────────────────────────────────────────────────────────────────
const ESPRESSO = '#2B211A';
const ORANGE = '#EA580C';
const CREAM = '#FAF8F4';
type Step = 'account' | 'checkout' | 'welcome';
type Plan = 'solo' | 'team';
// Giga's flower mark, tinted via a mask so it can be white on the orange chip.
function GigaMark({
  size = 20,
  color = '#FFFFFF'
}: {
  size?: number;
  color?: string;
}) {
  return <span className="inline-block shrink-0" style={{
    width: size,
    height: size,
    backgroundColor: color,
    WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
    maskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain'
  }} data-id="element-13113" data-mp="17jg9pngjkuli" />;
}
// The Giga wordmark used in the flow's top-left — identical treatment to the
// site-wide header in components/Layout.tsx (orange flower mark + wordmark).
function GigaWordmark() {
  return <span className="inline-flex items-center gap-2" data-id="element-13114" data-mp="gntw8x6t85wi">
      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="h-7 w-7 object-contain" data-id="element-13115" data-mp="3p2lqebeod8gj" />
      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-6 object-contain" data-id="element-13116" data-mp="2vx7n34coe4v" />
    </span>;
}
// The official multi-color Google "G" — inline SVG so it always renders with
// the correct brand colors (the icon CDN only serves a flat single-color mark).
function SlackMark({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={`shrink-0 ${className}`} aria-hidden="true" data-id="element-13117" data-mp="1ltjs10migxsj">
      <path fill="#36C5F0" d="M5.04 15.17a2.52 2.52 0 1 1-2.52-2.52h2.52v2.52Zm1.27 0a2.52 2.52 0 0 1 5.04 0v6.31a2.52 2.52 0 1 1-5.04 0v-6.31Z" data-id="element-13118" data-mp="17t92q20unr9h" />
      <path fill="#2EB67D" d="M8.83 5.04a2.52 2.52 0 1 1 2.52-2.52v2.52H8.83Zm0 1.27a2.52 2.52 0 0 1 0 5.04H2.52a2.52 2.52 0 1 1 0-5.04h6.31Z" data-id="element-13119" data-mp="1bvm6wlzy7h45" />
      <path fill="#ECB22E" d="M18.96 8.83a2.52 2.52 0 1 1 2.52 2.52h-2.52V8.83Zm-1.27 0a2.52 2.52 0 0 1-5.04 0V2.52a2.52 2.52 0 1 1 5.04 0v6.31Z" data-id="element-13120" data-mp="24jib0brukbay" />
      <path fill="#E01E5A" d="M15.17 18.96a2.52 2.52 0 1 1-2.52 2.52v-2.52h2.52Zm0-1.27a2.52 2.52 0 0 1 0-5.04h6.31a2.52 2.52 0 1 1 0 5.04h-6.31Z" data-id="element-13121" data-mp="1qrbv8qslw6c1" />
    </svg>;
}
// A faint, blurred stand-in for the signed-in workspace that sits behind the
// modal steps — establishes the "you're almost in the product" feel without
// building a whole dashboard. Purely decorative.
function WorkspaceBackdrop() {
  return <div className="absolute inset-0 hidden overflow-hidden bg-[#F4F0EA] md:block" aria-hidden="true" data-id="element-13122" data-mp="zmhdhoj9ev5h">
      <div className="cream-grid" data-id="element-13123" data-mp="ultk4w7k6283" />
      <div className="mx-auto max-w-[1280px] px-10 pt-24 opacity-60 blur-[3px]" data-id="element-13124" data-mp="uoxxaj3048aw">
        <div className="mb-8 h-9 w-56 rounded-lg bg-white/70" data-id="element-13125" data-mp="pns1udhyi6xo" />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3" data-id="element-13126" data-mp="lmyi7wbjreal">
          {Array.from({
          length: 6
        }).map((_, i) => <div key={i} className="h-52 rounded-2xl border border-neutral-200/70 bg-white/70 p-6" data-id="element-13127" data-mp="1ibnvtag55kqr" data-mpi={i}>
              <div className="mb-4 flex items-center gap-3" data-id="element-13128" data-mp="1dduuaups9ewt">
                <div className="h-9 w-9 rounded-lg bg-neutral-200/80" data-id="element-13129" data-mp="18lkxcuq93el1" />
                <div className="flex flex-col gap-1.5" data-id="element-13130" data-mp="14kr7hvt8zppi">
                  <div className="h-3 w-24 rounded bg-neutral-200/80" data-id="element-13131" data-mp="14k9dz6xyzpla" />
                  <div className="h-2.5 w-16 rounded bg-neutral-200/60" data-id="element-13132" data-mp="212x79p9h2jkz" />
                </div>
              </div>
              <div className="space-y-2.5" data-id="element-13133" data-mp="1wb5h3fu8afvd">
                <div className="h-2.5 w-full rounded bg-neutral-200/60" data-id="element-13134" data-mp="1wa47lfsv21fr" />
                <div className="h-2.5 w-11/12 rounded bg-neutral-200/60" data-id="element-13135" data-mp="3f0jgftu4o45e" />
                <div className="h-2.5 w-3/4 rounded bg-neutral-200/60" data-id="element-13136" data-mp="3jyteqdb5fkv8" />
              </div>
            </div>)}
        </div>
      </div>
      {/* soft cream veil so foreground cards stay legible */}
      <div className="absolute inset-0 bg-[#F4F0EA]/55" data-id="element-13137" data-mp="3szj2ctq9a4xv" />
    </div>;
}
// A subtle "Issue? Ask a human for help" footer link, matching the reference.
function HelpFooter({
  dark = false
}: {
  dark?: boolean;
}) {
  return <button type="button" className={`mx-auto mt-8 flex items-center gap-2 text-sm font-medium transition-colors ${dark ? 'text-white/60 hover:text-white' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-13138" data-mp="6poyehafasvr">
      <MessageCircleIcon size={16} data-id="element-13139" data-mp="fj1v7ublyen4" />
      Issue? Ask a human for help
    </button>;
}
// ── Step 1: account creation ──────────────────────────────────────────────
function AccountStep({
  name,
  setName,
  email,
  setEmail,
  onContinue
}: {
  name: string;
  setName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  onContinue: () => void;
}) {
  const canContinue = name.trim().length > 0 && /\S+@\S+\.\S+/.test(email);
  // On mobile the floating-card treatment reads as cramped chrome, so the form
  // goes full-bleed on a plain white page; the card + blurred workspace
  // backdrop only appear from md up.
  return <div className="relative min-h-screen w-full bg-white md:bg-transparent" data-id="element-13140" data-mp="34b3vkmh1hfd8">
      <WorkspaceBackdrop data-id="element-13141" data-mp="34d89lx8njur8" />
      <div className="relative flex min-h-screen flex-col px-5 py-6 md:px-6 md:py-8" data-id="element-13142" data-mp="30abmbt863agy">
        <header data-id="element-13143" data-mp="3e9ba6ynuvlc2">
          <GigaWordmark data-id="element-13144" data-mp="3e9tf0jol37vl" />
        </header>
        <main className="flex flex-1 items-center justify-center py-6 md:py-10" data-id="element-13145" data-mp="3e8s187g6o7a6">
          <div className="w-full max-w-4xl" data-id="element-13146" data-mp="39brv5wjc34j2">
            <motion.div initial={{
            opacity: 0,
            y: 16,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="grid grid-cols-1 bg-white md:grid-cols-[1.05fr_0.95fr] md:overflow-hidden md:rounded-[28px] md:border md:border-neutral-200/80 md:shadow-[0_40px_100px_-30px_rgba(43,33,26,0.45)]" data-id="element-13147" data-mp="1jviqydftchqv">
              {/* Left: the form */}
              <form className="flex flex-col py-4 md:p-10" onSubmit={e => {
              e.preventDefault();
              if (canContinue) onContinue();
            }} data-id="element-13148" data-mp="3lskggj12f3ww">
                <h1 className="font-title text-[26px] leading-tight text-[#2B211A] md:text-3xl" data-id="element-13149" data-mp="3cse9lfzvh26p">
                  Let’s get you started
                </h1>
                <p className="mt-1.5 text-sm font-medium text-neutral-500" data-id="element-13150" data-mp="2xwf0q34xuy0i">
                  Takes less than 2 minutes
                </p>

                <label className="mt-8 block text-sm font-semibold text-[#2B211A]" data-id="element-13151" data-mp="w3qxvuw7icl9">
                  How should we call you?{' '}
                  <span className="text-[#EA580C]" data-id="element-13152" data-mp="1133kqfn2vjnu">*</span>
                </label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Faith" autoFocus className="mt-2 w-full rounded-xl border border-neutral-300 bg-white px-4 py-3.5 text-[15px] text-[#2B211A] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-4 focus:ring-[#EA580C]/15" data-id="element-13153" data-mp="3kymgl3r6rrpt" />

                <label className="mt-5 block text-sm font-semibold text-[#2B211A]" data-id="element-13154" data-mp="3696tbxqv64ev">
                  What’s your best email?{' '}
                  <span className="text-[#EA580C]" data-id="element-13155" data-mp="318slafp9iugu">*</span>
                </label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="faith@company.com" className="mt-2 w-full rounded-xl border border-neutral-300 bg-white px-4 py-3.5 text-[15px] text-[#2B211A] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-4 focus:ring-[#EA580C]/15" data-id="element-13156" data-mp="2x807ra9cy3er" />

                <div className="my-6 flex items-center gap-4 text-xs font-semibold uppercase tracking-wide text-neutral-400" data-id="element-13157" data-mp="lj8y3iutwv68">
                  <span className="h-px flex-1 bg-neutral-200" data-id="element-13158" data-mp="t2ibroadbg13" />
                  or
                  <span className="h-px flex-1 bg-neutral-200" data-id="element-13159" data-mp="y1bdykk7fkly" />
                </div>

                {/* Slack is how Giga connects to your workspace, so the SSO
                 path matches: continue with the same Slack account. */}
                <button type="button" onClick={onContinue} className="flex w-full items-center justify-center gap-3 rounded-xl border border-neutral-300 bg-white px-4 py-3.5 text-[15px] font-semibold text-[#2B211A] transition-colors hover:bg-neutral-50" data-id="element-13160" data-mp="17u90d1sbhtpu">
                  <SlackMark className="h-5 w-5" data-id="element-13161" data-mp="k2bya2eo6g1x" />
                  Continue with Slack
                </button>

                <button type="submit" disabled={!canContinue} className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#EA580C] px-4 py-4 text-[15px] font-bold text-white shadow-[0_12px_30px_-8px_rgba(234,88,12,0.6)] transition-all hover:bg-[#d24e08] disabled:cursor-not-allowed disabled:opacity-40" data-id="element-13162" data-mp="p2g8jaoziejp">
                  Get 7-day free trial
                  <ArrowRightIcon size={18} strokeWidth={2.5} data-id="element-13163" data-mp="1hh7niqr1natp" />
                </button>
              </form>

              {/* Right: social proof — on mobile it condenses to a single
               compact testimonial card so the form stays the hero */}
              <div className="mt-8 flex flex-col justify-between rounded-2xl border border-neutral-200/80 bg-[#FAF8F4] p-6 md:mt-0 md:rounded-none md:border-0 md:border-l md:border-neutral-200/80 md:p-10" data-id="element-13164" data-mp="1r6he2268olfp">
                <div className="hidden md:block" data-id="element-13165" data-mp="yiv8iodgb6q7">
                  <p className="text-sm font-medium italic text-neutral-500" data-id="element-13166" data-mp="13heyczv612nu">
                    Trusted by 500+ teams &amp; AI agents
                  </p>
                  <p className="mt-3 font-title text-2xl italic leading-snug text-[#EA580C]" data-id="element-13167" data-mp="18eesqdinkgz4">
                    “Like a second brain for my whole company.”
                  </p>
                  <ul className="mt-6 space-y-3" data-id="element-13168" data-mp="2ju3gh73qdk0z">
                    {['One assistant across every tool you use', 'Answers from your real work, not the web', 'Automates the busywork before you ask'].map(item => <li key={item} className="flex items-start gap-2.5 text-[15px] font-medium text-[#2B211A]" data-id="element-13169" data-mp="3a02aec1p4wb">
                        <CheckCircle2Icon size={20} className="mt-0.5 shrink-0 text-[#EA580C]" data-id="element-13170" data-mp="3um2u58pw8sto" />
                        {item}
                      </li>)}
                  </ul>
                </div>
                <div className="md:mt-8 md:border-t md:border-neutral-200/80 md:pt-6" data-id="element-13171" data-mp="3losgjgplsrll">
                  <div className="flex gap-0.5" data-id="element-13172" data-mp="3gmthgj0wcgug">
                    {Array.from({
                    length: 5
                  }).map((_, i) => <StarIcon key={i} size={16} className="fill-[#F5A623] text-[#F5A623]" data-id="element-13173" data-mp="3br4kmng9he1u" data-mpi={i} />)}
                  </div>
                  <p className="mt-3 text-[15px] italic text-neutral-600" data-id="element-13174" data-mp="19xojgwmjsgsm">
                    “The first 30 minutes saved me 8 hours of work.”
                  </p>
                  <div className="mt-4 flex items-center gap-3" data-id="element-13175" data-mp="14wr38pl3lwtr">
                    <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Frame_27.png" alt="" className="h-10 w-10 rounded-full object-cover" data-id="element-13176" data-mp="2zeacgzyc8hcj" />
                    <div className="leading-tight" data-id="element-13177" data-mp="2pet9wredujks">
                      <p className="text-sm font-bold text-[#2B211A]" data-id="element-13178" data-mp="2uf7f81olnff4">
                        Max Schmitt
                      </p>
                      <p className="text-xs font-medium text-neutral-500" data-id="element-13179" data-mp="3i6cwmla6ufrj">
                        Founder of Cakedesk
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <p className="mt-6 text-center text-sm font-medium text-neutral-500" data-id="element-13180" data-mp="3d5p8q8h9af1j">
              Already have an account?{' '}
              <Link to="/app-screen-mockups/sign-in" className="font-semibold text-[#EA580C] hover:underline" data-id="element-13181" data-mp="1xox20jiu2zu8">
                Log in
              </Link>
            </p>
            <HelpFooter data-id="element-13182" data-mp="1u6zovenpikmc" />
          </div>
        </main>
      </div>
    </div>;
}
// ── Step 2: checkout ──────────────────────────────────────────────────────
const PLANS: Record<Plan, {
  name: string;
  monthly: number;
  wasMonthly: number;
}> = {
  solo: {
    name: 'Solo',
    monthly: 29,
    wasMonthly: 49
  },
  team: {
    name: 'Team',
    monthly: 49,
    wasMonthly: 97
  }
};
// The Stripe "Link" wordmark used on the express-pay button — lowercase, tight,
// in Link's signature dark ink so the button reads as the real thing.
function LinkMark({
  className = ''
}: {
  className?: string;
}) {
  return <span className={`font-semibold lowercase tracking-tight ${className}`} style={{
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }} data-id="element-13183" data-mp="1l85uluxum6by">link</span>;
}
// The Apple mark for the Apple Pay wallet button, inline so it renders crisp
// white on the black pill.
function AppleLogo({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 384 512" className={className} fill="currentColor" aria-hidden="true" data-id="element-13184" data-mp="3n5zlgzw3f0mi">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" data-id="element-13185" data-mp="3r71lvk1qxiq2" />
    </svg>;
}
const COUNTRIES = ['United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'Netherlands', 'Ireland', 'Singapore', 'India'];
// The Stripe-style selection dot used on each payment-method row.
function RadioDot({
  on
}: {
  on: boolean;
}) {
  return <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors ${on ? 'border-[#EA580C]' : 'border-neutral-400'}`} aria-hidden="true" data-id="element-13186" data-mp="2kjxotj4a2cni">
      {on && <span className="h-2 w-2 rounded-full bg-[#EA580C]" data-id="element-13187" data-mp="2hypw645q616r" />}
    </span>;
}
function CheckoutStep({
  plan,
  setPlan,
  onStartTrial
}: {
  plan: Plan;
  setPlan: (p: Plan) => void;
  onStartTrial: () => void;
}) {
  useEffect(() => {
    trackTrialCheckoutViewed(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Mobile-only "View details" disclosure for the order breakdown, mirroring
  // Stripe Checkout's collapsed summary on small screens.
  const [detailsOpen, setDetailsOpen] = useState(false);
  // The Payment-method accordion selection — Stripe renders Card / Google Pay /
  // Bank as radio rows, with only the selected one expanded.
  const [payMethod, setPayMethod] = useState<'card' | 'google' | 'bank'>('card');
  const selected = PLANS[plan];
  // Monthly-only billing — the subscription is a flat monthly amount.
  const fullMonthly = selected.monthly; // e.g. 49
  // Trial ends 7 days from "today" (the metadata date is July 10, 2026).
  const trialEnd = new Date('2026-07-17T00:00:00');
  const trialEndLabel = trialEnd.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  // Stripe Checkout renders on a full-height split with a clean white product
  // pane on the left and the payment form on the right — recreated faithfully
  // here, rebranded to Giga's orange accent.
  const inputClass = 'w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-[15px] text-[#30313D] outline-none transition-shadow placeholder:text-neutral-400 focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/30';
  const labelClass = 'block text-[13px] font-medium text-[#30313D]';
  return <div className="relative min-h-screen w-full bg-white" data-id="element-13188" data-mp="p9papufys9nu">
      <div className="mx-auto grid min-h-screen w-full grid-cols-1 lg:grid-cols-2" data-id="element-13189" data-mp="pa8i4h7znfnm">
        {/* ── Left: dark product / order summary (Stripe Checkout). On mobile
         it collapses to a compact header with a "View details" disclosure,
         exactly like Stripe's small-screen checkout. ─────────────────────── */}
        <aside className="flex flex-col bg-[#1B130D] px-5 py-5 text-white sm:px-10 lg:py-16 lg:pl-[max(2.5rem,calc((100vw-1180px)/2+2.5rem))] lg:pr-16" data-id="element-13190" data-mp="3suprro1ox85d">
          <div className="flex flex-1 flex-col" data-id="element-13191" data-mp="5oyiefyhiiss">
            {/* back + merchant — orange brand avatar with the white flower
             mark, next to the business name */}
            <div className="flex items-center gap-3" data-id="element-13192" data-mp="fltdeovju12b">
              <Link to="/" aria-label="Back" className="text-white/50 transition-colors hover:text-white" data-id="element-13193" data-mp="fk8ll70mcw85">
                <ArrowLeftIcon size={18} data-id="element-13194" data-mp="alfhu6b4wjl6" />
              </Link>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EA580C]" data-id="element-13195" data-mp="odda75gmm0un">
                <GigaMark size={14} data-id="element-13196" data-mp="kecrro229086" />
              </span>
              <span className="text-[15px] font-medium text-white" data-id="element-13197" data-mp="yckuydpowxrs">Giga</span>
            </div>

            {/* headline price — compact on mobile, tall on desktop */}
            <div className="mt-6 lg:mt-12" data-id="element-13198" data-mp="tbn65ia6d5hb">
              <p className="text-[14px] text-white/60 lg:text-[15px]" data-id="element-13199" data-mp="20qyptcorxvpo">
                Subscribe to Giga
              </p>
              <div className="mt-1 flex items-start gap-2" data-id="element-13200" data-mp="24qh9k54v00qx">
                <span className="text-[32px] font-bold leading-none tracking-tight lg:text-[44px]" data-id="element-13201" data-mp="29r51qu4tqnmq">
                  US${fullMonthly}
                </span>
                <span className="mt-0.5 text-[13px] leading-tight text-white/60 lg:mt-1 lg:text-[15px]" data-id="element-13202" data-mp="2ep6s804kxp88">
                  per
                  <br data-id="element-13203" data-mp="1m1apadof3130" />
                  month
                </span>
              </div>
              <p className="mt-2 text-[13px] text-white/50 lg:mt-3 lg:text-[14px]" data-id="element-13204" data-mp="1lyy7q1mkmsqh">
                7-day free trial · US$0.00 due today
              </p>
            </div>

            {/* mobile-only details disclosure */}
            <button type="button" onClick={() => setDetailsOpen(o => !o)} className="mt-4 flex items-center gap-1.5 self-start text-[13px] font-medium text-white/60 transition-colors hover:text-white lg:hidden" aria-expanded={detailsOpen} data-id="element-13205" data-mp="yc897a9flgam">
              {detailsOpen ? 'Hide details' : 'View details'}
              <ChevronDownIcon size={15} className={`transition-transform ${detailsOpen ? 'rotate-180' : ''}`} data-id="element-13206" data-mp="qrnxlln4ohwk" />
            </button>

            {/* breakdown — always visible on desktop, behind the disclosure on mobile */}
            <div className={`${detailsOpen ? 'block' : 'hidden'} lg:block`} data-id="element-13207" data-mp="gt83rawpuujd">
              {/* line item */}
              <div className="mt-6 flex items-start justify-between gap-4 lg:mt-12" data-id="element-13208" data-mp="ctzae75l8yoc">
                <div data-id="element-13209" data-mp="80oa6ova7g5e">
                  <p className="text-[15px] font-medium text-white" data-id="element-13210" data-mp="80ekz3i29d0r">Giga</p>
                  <p className="mt-0.5 text-[13px] text-white/50" data-id="element-13211" data-mp="33o3cbqly9d8">
                    Billed monthly
                  </p>
                </div>
                <p className="text-[15px] font-medium text-white" data-id="element-13212" data-mp="3u7344cwas9cy">
                  US${fullMonthly}.00
                </p>
              </div>

              {/* subtotal */}
              <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-6" data-id="element-13213" data-mp="3l916m7brvyuf">
                <span className="text-[15px] font-medium text-white" data-id="element-13214" data-mp="2a402ze2mnush">Subtotal</span>
                <span className="text-[15px] font-medium text-white" data-id="element-13215" data-mp="25680xr89qsak">
                  US${fullMonthly}.00
                </span>
              </div>

              {/* promotion code */}
              <div className="mt-5" data-id="element-13216" data-mp="1w3yupdk19l2z">
                <button type="button" className="rounded-lg bg-white/[0.07] px-4 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-white/10" data-id="element-13217" data-mp="1w74k6twkqoz8">
                  Add promotion code
                </button>
              </div>

              {/* totals — after-trial total first, then due today */}
              <div className="mt-5 space-y-2.5 border-t border-white/10 pt-5" data-id="element-13218" data-mp="1deiqog953aht">
                <div className="flex items-center justify-between" data-id="element-13219" data-mp="1dcfqx4i6q2zw">
                  <span className="text-[14px] text-white/60" data-id="element-13220" data-mp="18e4h2trw3sho">
                    Total after trial
                  </span>
                  <span className="text-[14px] text-white/60" data-id="element-13221" data-mp="13ifpbcm43fvx">
                    US${fullMonthly}.00 / month
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-13222" data-mp="3k1zs77ink4vj">
                  <span className="text-[15px] font-medium text-white" data-id="element-13223" data-mp="3p1vwz8z6c0c5">
                    Total due today
                  </span>
                  <span className="text-[15px] font-medium text-white" data-id="element-13224" data-mp="3t2fuwerkn7zc">US$0.00</span>
                </div>
              </div>
            </div>

            {/* footer pinned to the bottom of the dark rail — desktop only;
             on mobile it moves below the payment form */}
            <div className="mt-auto hidden items-center gap-4 pt-14 text-[12px] text-white/40 lg:flex" data-id="element-13225" data-mp="35dd5ululycut">
              <span className="flex items-center gap-1.5" data-id="element-13226" data-mp="3a54u2pqak6hs">
                Powered by <span className="font-bold text-white/70" data-id="element-13227" data-mp="2dmp5hc0rdbi7">stripe</span>
              </span>
              <span className="h-3 w-px bg-white/20" data-id="element-13228" data-mp="2dkw7sm84i1mc" />
              <button type="button" className="hover:text-white/70" data-id="element-13229" data-mp="2hn946k0zcn6g">Terms</button>
              <button type="button" className="hover:text-white/70" data-id="element-13230" data-mp="3kyg688znx08y">Privacy</button>
            </div>
          </div>
        </aside>

        {/* ── Right: white payment form ─────────────────────────────────── */}
        <div className="px-5 py-8 sm:px-10 lg:py-16 lg:pl-16 lg:pr-[max(2.5rem,calc((100vw-1180px)/2+2.5rem))]" data-id="element-13231" data-mp="3unzpvgv78y4v">
          <div className="mx-auto w-full max-w-md" data-id="element-13232" data-mp="3pqgcej6bspdz">
            {/* Express checkout — Apple Pay + Link, exactly as Stripe renders
             wallets above the card form */}
            <div className="grid grid-cols-2 gap-3" data-id="element-13233" data-mp="2x229bp0dr2vk">
              <button type="button" onClick={onStartTrial} aria-label="Pay with Apple Pay" className="flex h-12 items-center justify-center rounded-lg bg-black text-white transition-opacity hover:opacity-85" data-id="element-13234" data-mp="2x3lvfw8pw859">
                <AppleLogo className="mr-1 h-[19px] w-[19px]" data-id="element-13235" data-mp="qat2r7d6tfn4" />
                <span className="text-[17px] font-medium" data-id="element-13236" data-mp="q8zw3s2kqvf4">Pay</span>
              </button>
              <button type="button" onClick={onStartTrial} aria-label="Pay with Link" className="flex h-12 items-center justify-center gap-1 rounded-lg bg-[#33DDB3] text-[#1A1A1A] transition-opacity hover:opacity-85" data-id="element-13237" data-mp="m8odslxop76t">
                <span className="text-[16px] font-medium" data-id="element-13238" data-mp="ypqrf2nxnpti">Pay with</span>
                <LinkMark className="text-[18px]" data-id="element-13239" data-mp="jvukn87ff58e" />
              </button>
            </div>

            {/* "OR" divider, exactly as Stripe labels it between wallets and the form */}
            <div className="my-6 flex items-center gap-4 text-[13px] font-medium text-[#6B7280]" data-id="element-13240" data-mp="1ciymj4b833fy">
              <span className="h-px flex-1 bg-neutral-200" data-id="element-13241" data-mp="1hil0jfyvknpt" />
              OR
              <span className="h-px flex-1 bg-neutral-200" data-id="element-13242" data-mp="12toutpkajmsd" />
            </div>

            <form onSubmit={e => {
            e.preventDefault();
            onStartTrial();
          }} data-id="element-13243" data-mp="12rkn0wj9u3mt">
              {/* ── Contact information ─────────────────────────────────────
               Stripe shows the signed-in email as a filled pill with an
               inline "Continue with Link" affordance. */}
              <h2 className="text-[15px] font-semibold text-[#30313D]" data-id="element-13244" data-mp="cl0rbtr8dfer">
                Contact information
              </h2>
              <div className="mt-2 flex items-center gap-3 rounded-lg bg-[#F6F8FA] px-3.5 py-3" data-id="element-13245" data-mp="7kd5dmhuzore">
                <span className="text-[13px] font-medium text-[#6B7280]" data-id="element-13246" data-mp="2ncumya2dxty">Email</span>
                <span className="min-w-0 flex-1 truncate text-[15px] text-[#30313D]" data-id="element-13247" data-mp="3ttebjngpnlni">
                  faith@company.com
                </span>
                <button type="button" className="shrink-0 text-[14px] font-medium text-[#30313D] underline decoration-neutral-300 underline-offset-2 transition-colors hover:decoration-neutral-500" data-id="element-13248" data-mp="va7hg1uct9al">
                  Continue with <LinkMark className="text-[14px]" data-id="element-13249" data-mp="lit7f04s0r37" />
                </button>
              </div>

              {/* ── Payment method ──────────────────────────────────────────
               A radio accordion: Card (selected/expanded), then Google Pay and
               Bank collapsed underneath — Stripe's canonical method list. */}
              <h2 className="mt-7 text-[15px] font-semibold text-[#30313D]" data-id="element-13250" data-mp="1hk61mwcesl2z">
                Payment method
              </h2>
              <div className="mt-2 divide-y divide-neutral-200 overflow-hidden rounded-xl border border-neutral-300" data-id="element-13251" data-mp="1llrcyd9aorb2">
                {/* Card */}
                <div data-id="element-13252" data-mp="1vj5qo53u31zo">
                  <button type="button" onClick={() => setPayMethod('card')} className="flex w-full items-center gap-3 px-4 py-3.5 text-left" aria-expanded={payMethod === 'card'} data-id="element-13253" data-mp="1vjfi7x4j1h7h">
                    <RadioDot on={payMethod === 'card'} data-id="element-13254" data-mp="24jlr15pa5t0b" />
                    <CreditCardIcon size={18} className="text-[#30313D]" data-id="element-13255" data-mp="24i0x9iasie3i" />
                    <span className="text-[15px] font-medium text-[#30313D]" data-id="element-13256" data-mp="29blomrhvo9tq">Card</span>
                  </button>
                  <AnimatePresence initial={false} data-id="element-13257" data-mp="2e95bgfsn5uxa">
                    {payMethod === 'card' && <motion.div key="card-body" initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.22,
                    ease: [0.22, 1, 0.36, 1]
                  }} className="overflow-hidden" data-id="element-13258" data-mp="2j9t0wx1823pe">
                        <div className="space-y-4 px-4 pb-4" data-id="element-13259" data-mp="23i8rko6ua4h1">
                          {/* Card information */}
                          <div data-id="element-13260" data-mp="2h9drlre4cyrw">
                            <label className={labelClass} data-id="element-13261" data-mp="2h8c76wb7qzxi">Card information</label>
                            <div className="mt-1.5 overflow-hidden rounded-lg border border-neutral-300 focus-within:border-[#EA580C] focus-within:ring-2 focus-within:ring-[#EA580C]/30" data-id="element-13262" data-mp="2caapaepix6nm">
                              <div className="flex items-center border-b border-neutral-200" data-id="element-13263" data-mp="2mabcwcufe8xm">
                                <input placeholder="1234 1234 1234 1234" inputMode="numeric" className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-13264" data-mp="wvd7sg9eo5q7" />
                                <div className="flex items-center gap-1 pr-3" aria-hidden="true" data-id="element-13265" data-mp="33jx2jm3pren0">
                                  <img src="https://cdn.simpleicons.org/visa/1434CB" alt="" className="h-4" data-id="element-13266" data-mp="38gdqogawxt03" />
                                  <img src="https://cdn.simpleicons.org/mastercard" alt="" className="h-5" data-id="element-13267" data-mp="2ts9gy1tu8rtg" />
                                  <img src="https://cdn.simpleicons.org/americanexpress/006FCF" alt="" className="h-5" data-id="element-13268" data-mp="2yq198wyeafy6" />
                                  <img src="https://cdn.simpleicons.org/discover/FF6000" alt="" className="h-4" data-id="element-13269" data-mp="2kugzos4k3a3f" />
                                </div>
                              </div>
                              <div className="flex" data-id="element-13270" data-mp="2aw19crdcs4uz">
                                <input placeholder="MM / YY" className="w-1/2 bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-13271" data-mp="2fs873wvlia1u" />
                                <input placeholder="CVC" inputMode="numeric" className="w-1/2 border-l border-neutral-200 bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-13272" data-mp="e1eiyl7jkki8" />
                              </div>
                            </div>
                          </div>

                          {/* Cardholder name */}
                          <div data-id="element-13273" data-mp="2o5rp2kkiyhy1">
                            <label className={labelClass} data-id="element-13274" data-mp="2j9b2husz4bn2">Cardholder name</label>
                            <input placeholder="Full name on card" className={`mt-1.5 ${inputClass}`} data-id="element-13275" data-mp="2e9oohj5bmrd7" />
                          </div>

                          {/* Country + postal code */}
                          <div data-id="element-13276" data-mp="29cy8evtinvrs">
                            <label className={labelClass} data-id="element-13277" data-mp="258i7za2o9gjy">Country or region</label>
                            <div className="mt-1.5 overflow-hidden rounded-lg border border-neutral-300 focus-within:border-[#EA580C] focus-within:ring-2 focus-within:ring-[#EA580C]/30" data-id="element-13278" data-mp="20a6u8o99alwg">
                              <div className="relative border-b border-neutral-200" data-id="element-13279" data-mp="3unyd8edh6i4k">
                                <select defaultValue="United States" className="w-full appearance-none bg-transparent px-3 py-2.5 pr-9 text-[15px] text-[#30313D] outline-none" data-id="element-13280" data-mp="3ulvhddpn5urx">
                                  {COUNTRIES.map(c => <option key={c} data-id="element-13281" data-mp="az3bem75gdpj">{c}</option>)}
                                </select>
                                <ChevronDownIcon size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-13282" data-mp="fu8sn7plx47a" />
                              </div>
                              <input placeholder="ZIP" className="w-full bg-transparent px-3 py-2.5 text-[15px] text-[#30313D] outline-none placeholder:text-neutral-400" data-id="element-13283" data-mp="3japnv68ptgnv" />
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Google Pay */}
                <button type="button" onClick={() => setPayMethod('google')} className="flex w-full items-center gap-3 px-4 py-3.5 text-left" data-id="element-13284" data-mp="18gaczmhz0cbl">
                  <RadioDot on={payMethod === 'google'} data-id="element-13285" data-mp="23k89s0a8aal6" />
                  <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/google.svg" alt="" aria-hidden="true" className="h-[18px] w-[18px]" data-id="element-13286" data-mp="23kqapa7u5czf" />
                  <span className="text-[15px] font-medium text-[#30313D]" data-id="element-13287" data-mp="1tux28i2zpdze">Google Pay</span>
                </button>

                {/* Bank */}
                <button type="button" onClick={() => setPayMethod('bank')} className="flex w-full items-center gap-3 px-4 py-3.5 text-left" data-id="element-13288" data-mp="1yr5bjeuiacyn">
                  <RadioDot on={payMethod === 'bank'} data-id="element-13289" data-mp="1pt33wgg9om2e" />
                  <LandmarkIcon size={18} className="text-[#30313D]" data-id="element-13290" data-mp="1pqgwqhxag8kp" />
                  <span className="text-[15px] font-medium text-[#30313D]" data-id="element-13291" data-mp="1awkqqpolvs0n">Bank</span>
                </button>
              </div>

              {/* Subscribe */}
              <button type="submit" className="mt-6 w-full rounded-lg bg-[#EA580C] py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#d24e08]" data-id="element-13292" data-mp="359k226lye024">
                Subscribe
              </button>

              <p className="mt-4 text-center text-[12px] leading-relaxed text-[#8792A2]" data-id="element-13293" data-mp="yka04hsf7hou">
                By confirming your subscription, you allow Giga to charge your card
                for this payment and future payments in accordance with their terms.
                Your 7-day trial is free — you won’t be charged until {trialEndLabel},
                and you can cancel anytime.
              </p>

              {/* Stripe footer — mobile only (desktop shows it in the dark rail) */}
              <div className="mt-8 flex items-center justify-center gap-4 text-[12px] text-neutral-400 lg:hidden" data-id="element-13294" data-mp="1dahb6cg3lyfi">
                <span className="flex items-center gap-1.5" data-id="element-13295" data-mp="1r8nzp1z5qm2i">
                  Powered by <span className="font-bold text-neutral-500" data-id="element-13296" data-mp="1mavy1g8pnll4">stripe</span>
                </span>
                <span className="h-3 w-px bg-neutral-200" data-id="element-13297" data-mp="3t0u364pxkd5m" />
                <button type="button" className="hover:text-neutral-600" data-id="element-13298" data-mp="3t1c2ja7w37hr">Terms</button>
                <button type="button" className="hover:text-neutral-600" data-id="element-13299" data-mp="3o3k37v4x0j3j">Privacy</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>;
}
// ── The bank 3DS authorisation modal that overlays checkout ────────────────
function AuthModal({
  onDone
}: {
  onDone: () => void;
}) {
  const [seconds, setSeconds] = useState(297); // 4:57
  useEffect(() => {
    // auto-authorise after a short beat so the flow keeps moving
    const done = setTimeout(onDone, 2600);
    const tick = setInterval(() => setSeconds(s => s > 0 ? s - 1 : 0), 1000);
    return () => {
      clearTimeout(done);
      clearInterval(tick);
    };
  }, [onDone]);
  const mm = Math.floor(seconds / 60);
  const ss = String(seconds % 60).padStart(2, '0');
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} data-id="element-13300" data-mp="2opdau9wl8mwh">
      <motion.div initial={{
      opacity: 0,
      scale: 0.94,
      y: 12
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 26
    }} className="w-full max-w-md overflow-hidden rounded-2xl bg-white p-8 text-center text-[#111] shadow-2xl" data-id="element-13301" data-mp="2yocgrkpyy4fs">
        <div className="flex items-center justify-between" data-id="element-13302" data-mp="2us8fdmd6lxs7">
          <span className="text-lg font-bold tracking-tight" data-id="element-13303" data-mp="2utt9javl3epj">YourBank</span>
          <span className="flex items-center gap-1.5 rounded bg-[#1434CB] px-2 py-1 text-[11px] font-bold text-white" data-id="element-13304" data-mp="3rgxvb7590gbj">
            <ShieldCheckIcon size={13} data-id="element-13305" data-mp="3mkz2n7cw0c5b" /> SECURE
          </span>
        </div>
        <div className="mt-8 flex justify-center" data-id="element-13306" data-mp="3hq39g3vwziqm">
          <div className="flex h-28 w-28 items-center justify-center rounded-full border-4 border-[#1434CB] text-2xl font-semibold text-[#111]" data-id="element-13307" data-mp="3dquhn4kfpuxp">
            {mm}:{ss}
          </div>
        </div>
        <h2 className="mt-6 text-xl font-bold leading-snug" data-id="element-13308" data-mp="9bkvxmn7fazm">
          Check your bank app to authorise this payment
        </h2>
        <div className="mt-6 rounded-xl border border-neutral-200 p-4 text-left" data-id="element-13309" data-mp="3pypj7ukv64y6">
          <div className="flex items-center justify-between" data-id="element-13310" data-mp="3uytzbjhv0xnv">
            <span className="font-bold" data-id="element-13311" data-mp="3gysopcbsmbv7">GIGA</span>
            <span className="font-semibold" data-id="element-13312" data-mp="3h043062v720k">− USD 0.00</span>
          </div>
          <p className="mt-1 text-sm text-neutral-500" data-id="element-13313" data-mp="3kzmc84lbponk">Today, 10:18</p>
        </div>
        <button onClick={onDone} className="mt-6 text-sm font-semibold text-[#EA580C] hover:underline" data-id="element-13314" data-mp="ckuksy4uf078">
          Cancel payment
        </button>
      </motion.div>
    </motion.div>;
}
// ── Step 4: welcome (video + goal question) ────────────────────────────────
function WelcomeStep({
  name,
  goal,
  setGoal,
  onContinue
}: {
  name: string;
  goal: string;
  setGoal: (v: string) => void;
  onContinue: () => void;
}) {
  const reduced = useReducedMotion();
  const displayName = name.trim() || 'there';
  return <div className="relative min-h-screen w-full" data-id="element-13315" data-mp="10af3rh0zxxgi">
      <WorkspaceBackdrop data-id="element-13316" data-mp="10c9p2wg6jrl4" />
      <div className="absolute inset-0 bg-black/30" data-id="element-13317" data-mp="2l0ars889vx69" />
      <div className="relative flex min-h-screen items-center justify-center px-6 py-12" data-id="element-13318" data-mp="2kyzosamnitg7">
        <motion.div initial={reduced ? false : {
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} className="w-full max-w-2xl overflow-hidden rounded-[28px] border border-white/[0.08] bg-[#221812] p-8 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.7)] md:p-10" data-id="element-13319" data-mp="2g0poxmpzf4o9">
          {/* progress bar */}
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10" data-id="element-13320" data-mp="2bzmczbb21ssb">
            <motion.div className="h-full rounded-full bg-[#EA580C]" initial={{
            width: '60%'
          }} animate={{
            width: '92%'
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} data-id="element-13321" data-mp="2bxjee2vu6rbz" />
          </div>

          <h1 className="mt-8 text-center font-title text-3xl text-white" data-id="element-13322" data-mp="3ifg8ny0mg07j">
            👋 Welcome to Giga, {displayName}!
          </h1>
          <p className="mt-2 text-center text-[15px] font-medium text-[#B7A392]" data-id="element-13323" data-mp="3ui94oxbityp8">
            Your free trial is active 😊 Here’s a quick welcome from the team:
          </p>

          {/* Faux welcome video */}
          <div className="relative mt-7 overflow-hidden rounded-2xl bg-black" data-id="element-13324" data-mp="8arc2f6xxyr7">
            <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80" alt="Welcome video from the Giga team" className="h-64 w-full object-cover md:h-80" data-id="element-13325" data-mp="88oos2x6ajq6" />
            <span className="absolute right-3 top-3 rounded-md bg-black/60 px-2 py-1 text-xs font-semibold text-white" data-id="element-13326" data-mp="3gquloqhm5xi6">
              1.25×
            </span>
            {/* faux control bar */}
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8 text-white" data-id="element-13327" data-mp="3lje1lu3rx4q5">
              <PauseIcon size={18} className="fill-white" data-id="element-13328" data-mp="2szyxs4ga3mg7" />
              <VolumeXIcon size={18} data-id="element-13329" data-mp="1a5vpybqss54g" />
              <span className="text-xs font-medium" data-id="element-13330" data-mp="1a4uh8dx97upw">0:05</span>
              <div className="mx-1 h-1 flex-1 overflow-hidden rounded-full bg-white/30" data-id="element-13331" data-mp="164tt21kcs55n">
                <div className="h-full w-[8%] rounded-full bg-white" data-id="element-13332" data-mp="115h6zj1b3244" />
              </div>
              <span className="text-xs font-medium" data-id="element-13333" data-mp="w6cw8nyqgwbo">1:24</span>
              <span className="rounded bg-[#EA580C] px-1.5 py-0.5 text-[10px] font-bold" data-id="element-13334" data-mp="w5upgxeg2zpi">
                CC
              </span>
              <SettingsIcon size={16} data-id="element-13335" data-mp="r94e2ndl4saf" />
              <MaximizeIcon size={16} data-id="element-13336" data-mp="mgb51nb32xsj" />
            </div>
          </div>

          {/* Goal question */}
          <label className="mt-7 block text-[15px] font-semibold text-white" data-id="element-13337" data-mp="ig13cmubqtjh">
            What do you want to achieve with Giga?{' '}
            <span className="text-[#EA580C]" data-id="element-13338" data-mp="dhppm10wryvz">*</span>
          </label>
          <textarea value={goal} onChange={e => setGoal(e.target.value)} rows={4} placeholder="Tell us about your goals — we’ll tailor Giga to you based on your answer." className="mt-3 w-full resize-none rounded-xl border border-white/[0.12] bg-black/25 px-4 py-3.5 text-[15px] text-white outline-none transition-shadow placeholder:text-[#8C7563] focus:border-[#EA580C] focus:ring-4 focus:ring-[#EA580C]/15" data-id="element-13339" data-mp="8fqqj3c7bo4u" />

          <button onClick={onContinue} disabled={goal.trim().length === 0} className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#EA580C] px-4 py-4 text-[15px] font-bold text-white shadow-[0_12px_30px_-8px_rgba(234,88,12,0.6)] transition-all hover:bg-[#d24e08] disabled:cursor-not-allowed disabled:opacity-40" data-id="element-13340" data-mp="1id6xi71y9rn0">
            Continue
            <ArrowRightIcon size={18} strokeWidth={2.5} data-id="element-13341" data-mp="znh1jgmgazti" />
          </button>
        </motion.div>
      </div>
    </div>;
}
export function TrialFlow() {
  const init = useScreenInit() as {
    step?: Step;
    showAuth?: boolean;
  };
  const navigate = useNavigate();
  // Other surfaces (e.g. the dashboard's billing modal) can deep-link straight
  // to a specific step via router state: navigate('/app-screen-mockups/trial', { state: { step } }).
  const location = useLocation();
  const routedStep = (location.state as {
    step?: Step;
  } | null)?.step;
  const [step, setStep] = useState<Step>(routedStep ?? init.step ?? 'account');
  const [showAuth, setShowAuth] = useState(!!init.showAuth);
  // form state, lifted so it persists across steps
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState<Plan>('team');
  const [goal, setGoal] = useState('');
  return <div className="min-h-screen w-full" data-id="element-13342" data-mp="pyjvid8hsgtf">
      <AnimatePresence mode="wait" data-id="element-13343" data-mp="pxscs2zzudkk">
        {step === 'account' && <motion.div key="account" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-13344" data-mp="13porn5cniqz0">
            <AccountStep name={name} setName={setName} email={email} setEmail={setEmail} onContinue={() => {
          trackTrialStarted(plan);
          identifyUser(email, {
            email,
            name
          });
          setStep('checkout');
        }} data-id="element-13345" data-mp="1dpz42nqv3ud4" />
          </motion.div>}
        {step === 'checkout' && <motion.div key="checkout" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-13346" data-mp="1bi50nbvxenhy">
            <CheckoutStep plan={plan} setPlan={setPlan} onStartTrial={() => {
          trackTrialSubscribed(plan);
          setShowAuth(true);
        }} data-id="element-13347" data-mp="1ld5hcpc85bwd" />
          </motion.div>}
        {step === 'welcome' && <motion.div key="welcome" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-13348" data-mp="1v49t7g6lsgmz">
            <WelcomeStep name={name} goal={goal} setGoal={setGoal} onContinue={() => navigate('/app-screen-mockups/dashboard')} data-id="element-13349" data-mp="245r9ayk0gibo" />
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-13350" data-mp="9s8c5ptw71qw">
        {showAuth && <AuthModal onDone={() => {
        setShowAuth(false);
        setStep('welcome');
      }} data-id="element-13351" data-mp="9tb4m26v5c8x" />}
      </AnimatePresence>
    </div>;
}